import { useLayoutEffect, useState, type RefObject } from 'react';
import { bindScrollAndResize, type PopoverPlacement } from '@/app/common/components/Popover';

const DROPDOWN_GAP = 4;

const scrollParentOf = (node: HTMLElement) => {
  let parent = node.parentElement;
  while (parent && !/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) parent = parent.parentElement;
  return parent;
};

const visibleBoundsOf = (scroller: HTMLElement | null) => {
  if (!scroller) return { top: 0, bottom: window.innerHeight };
  const { top, bottom } = scroller.getBoundingClientRect();
  const stickyFooter = Array.from(scroller.children).find((child) => {
    const style = getComputedStyle(child);
    return style.position === 'sticky' && style.bottom !== 'auto';
  });
  return { top, bottom: stickyFooter?.getBoundingClientRect().top ?? bottom };
};

const sideWithRoom = (anchor: DOMRect, height: number, bounds: { top: number; bottom: number }): PopoverPlacement => {
  const fitsBelow = bounds.bottom - anchor.bottom >= height + DROPDOWN_GAP;
  const fitsAbove = anchor.top - bounds.top >= height + DROPDOWN_GAP;
  return !fitsBelow && fitsAbove ? 'top' : 'bottom';
};

export const useDropdownSide = (
  anchorRef: RefObject<HTMLElement | null>,
  dropdownRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  contentKey: unknown
): PopoverPlacement => {
  const [side, setSide] = useState<PopoverPlacement>('bottom');

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!isOpen || !anchor) return undefined;
    const scroller = scrollParentOf(anchor);
    const place = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;
      setSide(sideWithRoom(anchor.getBoundingClientRect(), dropdown.offsetHeight, visibleBoundsOf(scroller)));
    };
    place();
    return bindScrollAndResize(place, place);
  }, [isOpen, contentKey, anchorRef, dropdownRef]);

  return side;
};
